import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Plus, MapPin } from 'lucide-react';
import { useTranslations } from '../../i18n';
import { Location } from '../../lib/supabase';
import { LocationItem } from './LocationItem';
import { useLocations } from '../../hooks/useCarData';

interface LocationSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    carId: string;
    onSelect: (location: Location) => void;
    onAddNew: () => void;
    expenseType?: string | null;
}

export function LocationSelectorModal({ isOpen, onClose, carId, onSelect, onAddNew, expenseType }: LocationSelectorModalProps) {
    const { t } = useTranslations();
    const [searchQuery, setSearchQuery] = useState('');
    const { data: locations = [], isLoading } = useLocations(carId);

    const searchInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // iOS Keyboard Fix: Use Visual Viewport API
    useEffect(() => {
        if (!isOpen) return;
        if (!window.visualViewport) return;

        const vvp = window.visualViewport;
        const handleViewportChange = () => {
            if (containerRef.current) {
                containerRef.current.style.height = `${vvp.height}px`;
                containerRef.current.style.top = `${vvp.offsetTop}px`;
                if (window.scrollY !== 0) {
                    window.scrollTo(0, 0);
                }
            }
        };

        vvp.addEventListener('resize', handleViewportChange);
        vvp.addEventListener('scroll', handleViewportChange);
        handleViewportChange();

        // Lock background thoroughly to prevent iOS bounce leakage
        const originalOverflow = document.documentElement.style.overflow;
        const originalBodyOverflow = document.body.style.overflow;
        const originalBodyPosition = document.body.style.position;
        const originalBodyWidth = document.body.style.width;
        const originalBodyHeight = document.body.style.height;

        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100dvh';

        return () => {
            vvp.removeEventListener('resize', handleViewportChange);
            vvp.removeEventListener('scroll', handleViewportChange);
            document.documentElement.style.overflow = originalOverflow;
            document.body.style.overflow = originalBodyOverflow;
            document.body.style.position = originalBodyPosition;
            document.body.style.width = originalBodyWidth;
            document.body.style.height = originalBodyHeight;
        };
    }, [isOpen]);

    // Auto-focus search input when opened
    useEffect(() => {
        if (isOpen) {
            // small timeout to allow layout transition on iOS
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen]);

    const { recommendedLocations, otherLocations } = useMemo(() => {
        if (!isOpen && locations.length === 0) return { recommendedLocations: [], otherLocations: [] };
        
        let filtered = locations;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            filtered = locations.filter(loc =>
                (loc.name && loc.name.toLowerCase().includes(q)) ||
                (loc.address && loc.address.toLowerCase().includes(q))
            );
        }

        if (!expenseType) {
            return { recommendedLocations: [], otherLocations: filtered };
        }

        const recommended: Location[] = [];
        const others: Location[] = [];

        filtered.forEach(loc => {
            const allTypes: string[] = (loc as any).all_expense_types || [];
            if (allTypes.includes(expenseType)) {
                recommended.push(loc);
            } else {
                others.push(loc);
            }
        });

        return { recommendedLocations: recommended, otherLocations: others };
    }, [locations, searchQuery, isOpen, expenseType]);

    if (!isOpen) return null;

    return (
        <div
            ref={containerRef}
            className="fixed inset-0 flex flex-col overflow-hidden text-gray-900 dark:text-gray-100 z-[100] bg-gradient-theme"
        >
            {/* Custom Search Navigation Bar */}
            <div className="flex-shrink-0 z-30 non-draggable">
                <div className="max-w-2xl mx-auto flex items-center px-4 h-[68px] gap-3">
                    <div className="relative flex-1 group">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t.locations.searchLocations || 'Search locations...'}
                            className="w-full pl-12 pr-4 py-2.5 bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl focus:ring-2 focus:ring-blue-500/50 dark:focus:ring-blue-400/50 focus:border-transparent transition-all text-gray-900 dark:text-gray-100 shadow-sm text-[15px]"
                        />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-blue-600 dark:text-blue-400 font-bold text-[15px] px-2 py-2 active:opacity-60 transition-opacity whitespace-nowrap"
                    >
                        {t.common?.cancel || 'Cancel'}
                    </button>
                </div>
            </div>

            <div className="flex-1 flex flex-col w-full overflow-hidden">
                {/* Results List (Scrollable Area) */}
                <div className="flex-1 overflow-y-auto pb-6 overscroll-none">
                    <div className="max-w-2xl mx-auto px-4 mt-2">
                        {/* Always visible "Create New" button as the first item */}
                        <div className="mb-4">
                            <button
                                onClick={onAddNew}
                                className="w-full p-3 bg-blue-600/10 dark:bg-blue-400/10 border border-blue-500/20 dark:border-blue-400/20 rounded-xl flex items-center gap-3 transition-all hover:bg-blue-600/20 dark:hover:bg-blue-400/20 active:scale-[0.99] group"
                            >
                                <div className="w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform">
                                    <Plus size={20} strokeWidth={3} className="text-white" />
                                </div>
                                <div className="text-left">
                                    <div className="font-bold text-blue-700 dark:text-blue-300 text-sm uppercase tracking-wider">
                                        {(t.locations as any)?.createNew || 'Create New Location'}
                                    </div>
                                </div>
                            </button>
                        </div>

                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-24">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
                                <p className="text-gray-500 text-sm font-medium italic">{t.common?.loading || 'Loading'}...</p>
                            </div>
                        ) : (searchQuery.length === 0 && locations.length === 0) ? (
                            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-white/5 flex items-center justify-center mb-4">
                                    <MapPin size={32} className="text-gray-400 dark:text-gray-600" />
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium leading-relaxed max-w-[240px]">
                                    {(t.locations as any)?.noLocations || 'No locations available'}
                                </p>
                            </div>
                        ) : (recommendedLocations.length === 0 && otherLocations.length === 0) ? (
                            <div className="flex flex-col items-center justify-center py-12 px-6 bg-white/50 dark:bg-slate-900/20 border border-dashed border-gray-200 dark:border-white/10 rounded-2xl">
                                <MapPin size={24} className="text-gray-300 dark:text-gray-600 mb-2" />
                                <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                                    {(t.locations as any)?.noResults || 'No locations found'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {recommendedLocations.length > 0 && (
                                    <div className="space-y-3">
                                        <h3 className="px-2 text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none">
                                            {(t.locations as any)?.recommendedLocations || "Ieteiktās"}
                                        </h3>
                                        <div className="divide-y divide-gray-100 dark:divide-white/5 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/5 rounded-2xl shadow-sm overflow-hidden">
                                            {recommendedLocations.map((loc) => (
                                                <LocationItem
                                                    key={loc.id}
                                                    location={loc}
                                                    onClick={() => onSelect(loc)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {otherLocations.length > 0 && (
                                    <div className="space-y-3">
                                        {recommendedLocations.length > 0 && (
                                            <h3 className="px-2 text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none">
                                                {(t.locations as any)?.otherLocations || "Citas lokācijas"}
                                            </h3>
                                        )}
                                        <div className="divide-y divide-gray-100 dark:divide-white/5 bg-white dark:bg-slate-900/40 border border-gray-100 dark:border-white/5 rounded-2xl shadow-sm overflow-hidden">
                                            {otherLocations.map((loc) => (
                                                <LocationItem
                                                    key={loc.id}
                                                    location={loc}
                                                    onClick={() => onSelect(loc)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
